"use client";

import { useMemo, useState } from "react";
import { usePathname } from "next/navigation";

import PriceToggleIcon from "@/components/icons/price-toggle-icon";
import { coverageLogosForRegion } from "@/lib/coverage-logos";
import { formatPrice } from "@/lib/format-price";
import type { PriceStory } from "@/lib/prices";
import { regionFromPathname } from "@/lib/region";
import { resolveLink } from "@/lib/storyblok-link";

function CheckIcon() {
  return (
    <svg
      width="13"
      height="10"
      viewBox="0 0 13 10"
      fill="none"
      aria-hidden
      className="shrink-0 text-green-600"
    >
      <path
        d="M1.5 5l3.2 3.2L11.5 1.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Row = {
  uuid: string;
  name: string;
  description: string | undefined;
  features: string[];
  cardPrice: string | undefined;
  cardMonthly: string | undefined;
  learnMore: { href: string; target?: string };
  learnMoreLabel: string;
};

function buildRow(price: PriceStory, region: "ie" | "uk"): Row {
  const c = price.content || {};
  const baseName = (region === "uk" && c.name_uk ? c.name_uk : c.name) || price.name;
  return {
    uuid: price.uuid,
    name: c.asterisk ? `${baseName}${c.asterisk}` : baseName,
    description: c.description,
    features: ((region === "uk" && c.features_uk ? c.features_uk : c.features) || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean),
    cardPrice: formatPrice(region === "uk" ? c.price_uk : c.price_ie, region),
    cardMonthly: formatPrice(
      region === "uk" ? c.monthly_price_uk : c.monthly_price_ie,
      region,
    ),
    learnMore: resolveLink(c.learn_more_link, region),
    learnMoreLabel:
      c.learn_more_label || (c.name ? `More about ${c.name}` : "Learn more"),
  };
}

export default function TreatmentPriceListView({
  title,
  prices,
  footnote,
}: {
  title?: string;
  prices: PriceStory[];
  footnote?: string;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const [openId, setOpenId] = useState<string | null>(null);

  // Hide prices missing a value for the current region. "FREE" / "0"
  // still pass through.
  const rows = useMemo(
    () =>
      prices
        .map((price) => buildRow(price, region))
        .filter((row) => Boolean(row.cardPrice)),
    [prices, region],
  );

  if (rows.length === 0) return null;

  const leftColumn = rows.filter((_, i) => i % 2 === 0);
  const rightColumn = rows.filter((_, i) => i % 2 === 1);

  const renderItem = (row: Row) => {
    const isOpen = openId === row.uuid;
    const hasExpandable =
      Boolean(row.description) || row.features.length > 0;

    if (!hasExpandable) {
      return (
        <li
          key={row.uuid}
          className="rounded-[12px] bg-white p-5 md:rounded-[16px] md:p-6 lg:rounded-[20px] lg:p-9"
        >
          <CardHeader
            name={row.name}
            cardPrice={row.cardPrice}
            cardMonthly={row.cardMonthly}
          />
        </li>
      );
    }

    return (
      <li
        key={row.uuid}
        className="rounded-[12px] bg-white md:rounded-[16px] lg:rounded-[20px]"
      >
        <button
          type="button"
          onClick={(e) => {
            const opening = !isOpen;
            setOpenId(opening ? row.uuid : null);
            if (opening) {
              const el = e.currentTarget;
              setTimeout(
                () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
                320,
              );
            }
          }}
          aria-expanded={isOpen}
          className="flex w-full scroll-mt-6 cursor-pointer items-center gap-4 rounded-[12px] p-5 text-left focus-visible:ring-2 focus-visible:ring-purple-03 focus-visible:ring-offset-2 focus-visible:outline-none md:rounded-[16px] md:p-6 lg:rounded-[20px] lg:p-9"
        >
          <CardHeader
            name={row.name}
            cardPrice={row.cardPrice}
            cardMonthly={row.cardMonthly}
          />
          <span className="ml-auto size-[28px] shrink-0 md:size-[32px] lg:size-[40px]">
            <PriceToggleIcon open={isOpen} />
          </span>
        </button>
        <div
          inert={!isOpen}
          className={`grid overflow-hidden rounded-b-[12px] transition-[grid-template-rows] duration-300 ease-out md:rounded-b-[16px] lg:rounded-b-[20px] ${
            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <ExpandedDetails row={row} region={region} />
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[77px]">
      {title && (
        <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-05">
          {title}
        </h2>
      )}

      {/* Above md, two independent flex columns so an expanded card grows only
          within its own column, with no gap left below a short sibling. */}
      <ul className="mt-6 flex flex-col gap-3 md:hidden">
        {rows.map(renderItem)}
      </ul>
      <div className="mt-6 hidden gap-3 md:mt-12 md:grid md:grid-cols-2 md:gap-4 lg:mt-16 lg:gap-[23px]">
        <ul className="flex flex-col gap-3 md:gap-4 lg:gap-[23px]">
          {leftColumn.map(renderItem)}
        </ul>
        <ul className="flex flex-col gap-3 md:gap-4 lg:gap-[23px]">
          {rightColumn.map(renderItem)}
        </ul>
      </div>

      {footnote && (
        <p className="mt-6 whitespace-pre-line text-button leading-[1.56] text-[#595959] md:mt-8 lg:mt-10">
          {footnote}
        </p>
      )}
    </div>
  );
}

function CardHeader({
  name,
  cardPrice,
  cardMonthly,
}: {
  name: string;
  cardPrice: string | undefined;
  cardMonthly: string | undefined;
}) {
  return (
    <div className="flex flex-1 flex-col gap-1 lg:gap-[19px]">
      <p className="text-h5 font-semibold leading-[1.2] text-black md:text-h4 lg:text-h4 lg:leading-[1.08]">
        {name}
      </p>
      {(cardPrice || cardMonthly) && (
        <div className="flex flex-wrap items-end gap-x-2 gap-y-1 lg:gap-x-[14px]">
          {cardPrice && (
            <p className="text-h5 font-extrabold leading-[1.2] tracking-[0.02em] text-purple-03 lg:text-[28px] lg:leading-[1.054]">
              {cardPrice}
            </p>
          )}
          {cardMonthly && (
            <p className="text-button leading-[1.4] text-[#595959] md:text-body">
              From {cardMonthly} per month
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ExpandedDetails({
  row,
  region,
}: {
  row: Row;
  region: "ie" | "uk";
}) {
  return (
    <div className="flex flex-col gap-6 px-5 pb-5 md:px-6 md:pb-6 lg:gap-8 lg:px-9 lg:pb-9">
      {row.description && (
        <p className="text-body leading-[1.4] text-[#595959] lg:max-w-[391px]">
          {row.description}
        </p>
      )}

      {row.features.length > 0 && (
        <ul className="grid grid-cols-1 gap-x-8 gap-y-1 lg:max-w-[480px] lg:grid-cols-2">
          {row.features.map((feat, i) => (
            <li key={`${i}-${feat}`} className="flex items-center gap-2.5">
              <CheckIcon />
              <span className="text-body leading-[1.56] text-[#595959]">
                {feat}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-[9px]">
        <p className="text-button leading-[1.4] text-[#595959]">Covered by</p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {coverageLogosForRegion(region).map((logo) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={logo.src}
              src={logo.src}
              alt={logo.alt}
              className={`${logo.heightClass} w-auto`}
            />
          ))}
        </div>
      </div>

      {row.learnMore.href !== "#" && (
        <a
          href={row.learnMore.href}
          {...(row.learnMore.target ? { target: row.learnMore.target } : {})}
          className="inline-flex w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline"
        >
          {row.learnMoreLabel}
          <ArrowIcon />
        </a>
      )}
    </div>
  );
}

